/**
 * NotificationsPage Component
 * Full-page notification center with tabs, filters, and bulk actions
 */

import React, { useState } from 'react';
import { Tabs, Select, Button, Checkbox, Empty, Spin, Space } from 'antd';
import {
  useNotifications,
  useBulkMarkAsRead,
  useBulkArchive,
  useBulkDelete,
} from '@/hooks/useNotifications';
import { NotificationItem } from '@/components/features/notifications/NotificationItem';
import { ModernLayout } from '@/components/layout/ModernLayout';
import type { NotificationQueryParams } from '@/api/types/notification.types';
import './NotificationsPage.css';

const { TabPane } = Tabs;
const { Option } = Select;

export const NotificationsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'unread' | 'archived'>(
    'all'
  );
  const [selectedCategory, setSelectedCategory] = useState<
    string | undefined
  >();
  const [selectedPriority, setSelectedPriority] = useState<
    string | undefined
  >();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Build query params
  const queryParams: NotificationQueryParams = {
    status_filter:
      activeTab === 'unread'
        ? 'UNREAD'
        : activeTab === 'archived'
          ? 'ARCHIVED'
          : undefined,
    category: selectedCategory,
    limit: 50,
  };

  const { data: notifications, isLoading } = useNotifications(queryParams);
  const { mutate: bulkMarkAsRead, isPending: isMarkingAsRead } =
    useBulkMarkAsRead();
  const { mutate: bulkArchive, isPending: isArchiving } = useBulkArchive();
  const { mutate: bulkDelete, isPending: isDeleting } = useBulkDelete();

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(notifications?.map((n) => n.id) || []);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectItem = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    }
  };

  const handleBulkMarkAsRead = () => {
    if (selectedIds.length === 0) return;
    bulkMarkAsRead(
      { notification_ids: selectedIds },
      {
        onSuccess: () => {
          setSelectedIds([]);
        },
      }
    );
  };

  const handleBulkArchive = () => {
    if (selectedIds.length === 0) return;
    bulkArchive(
      { notification_ids: selectedIds },
      {
        onSuccess: () => {
          setSelectedIds([]);
        },
      }
    );
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    bulkDelete(
      { notification_ids: selectedIds },
      {
        onSuccess: () => {
          setSelectedIds([]);
        },
      }
    );
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key as 'all' | 'unread' | 'archived');
    setSelectedIds([]); // Clear selection on tab change
  };

  const allSelected =
    notifications && notifications.length > 0 &&
    selectedIds.length === notifications.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  return (
    <ModernLayout>
      <div className="notifications-page">
        {/* Header */}
        <div className="notifications-page-header">
          <h1>Notifications</h1>
        </div>

        {/* Tabs */}
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="All" key="all" />
          <TabPane tab="Unread" key="unread" />
          <TabPane tab="Archived" key="archived" />
        </Tabs>

        {/* Filters */}
        <div className="notifications-page-filters">
          <Space>
            <Select
              placeholder="Category"
              allowClear
              style={{ width: 200 }}
              value={selectedCategory}
              onChange={setSelectedCategory}
            >
              <Option value="Notifications.notification_category.SYSTEM">
                System
              </Option>
              <Option value="Notifications.notification_category.TICKET">
                Tickets
              </Option>
              <Option value="Notifications.notification_category.LEAD">
                Leads
              </Option>
              <Option value="Notifications.notification_category.CHAT">
                Chat
              </Option>
              <Option value="Notifications.notification_category.CALENDAR">
                Calendar
              </Option>
              <Option value="Notifications.notification_category.USER">
                User
              </Option>
            </Select>

            <Select
              placeholder="Priority"
              allowClear
              style={{ width: 150 }}
              value={selectedPriority}
              onChange={setSelectedPriority}
            >
              <Option value="Notifications.notification_priority.LOW">
                Low
              </Option>
              <Option value="Notifications.notification_priority.NORMAL">
                Normal
              </Option>
              <Option value="Notifications.notification_priority.HIGH">
                High
              </Option>
              <Option value="Notifications.notification_priority.URGENT">
                Urgent
              </Option>
            </Select>
          </Space>
        </div>

        {/* Bulk Actions */}
        {selectedIds.length > 0 && (
          <div className="notifications-page-bulk-actions">
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={(e) => handleSelectAll(e.target.checked)}
            >
              {allSelected
                ? 'Deselect all'
                : `Selected ${selectedIds.length} of ${notifications?.length || 0}`}
            </Checkbox>

            <Space>
              <Button
                onClick={handleBulkMarkAsRead}
                loading={isMarkingAsRead}
                disabled={isArchiving || isDeleting}
              >
                Mark as read
              </Button>
              <Button
                onClick={handleBulkArchive}
                loading={isArchiving}
                disabled={isMarkingAsRead || isDeleting}
              >
                Archive
              </Button>
              <Button
                danger
                onClick={handleBulkDelete}
                loading={isDeleting}
                disabled={isMarkingAsRead || isArchiving}
              >
                Delete
              </Button>
            </Space>
          </div>
        )}

        {/* Notification List */}
        <div className="notifications-page-list">
          {isLoading && (
            <div className="notifications-page-loading">
              <Spin size="large" />
            </div>
          )}

          {!isLoading && !notifications?.length && (
            <Empty
              description={
                activeTab === 'unread'
                  ? 'No unread notifications'
                  : activeTab === 'archived'
                    ? 'No archived notifications'
                    : 'No notifications'
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}

          {notifications?.map((notification) => (
            <div key={notification.id} className="notifications-page-list-item">
              <Checkbox
                checked={selectedIds.includes(notification.id)}
                onChange={(e) =>
                  handleSelectItem(notification.id, e.target.checked)
                }
                className="notifications-page-checkbox"
              />
              <div className="notifications-page-item-content">
                <NotificationItem notification={notification} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </ModernLayout>
  );
};
